import { fetchWithAuth } from "../utils/fetch/FetchWithAuth";
import { prepareBaseGetListParams } from "../utils/fetch/PrepareBaseGetListParams";
import moment from "moment/moment";
import { Constants } from "../utils/Constants";
import { PlanogramTransformUtils } from "../pages/planograms/utils/PlanogramTransformUtils";

const preparePlanogramForRequest = planogramData => {
    if (!planogramData) {
        return null;
    }

    const hasGridCells =
        Array.isArray(planogramData.cells) &&
        Array.isArray(planogramData.cells[0]);

    if (!hasGridCells) {
        return planogramData;
    }

    return PlanogramTransformUtils.createApiPlanogram(planogramData);
};

export const PlanogramPendingService = {
    getList: (params = {}) => {
        const url = `/portal/planogram-pendings`;
        const queryParams = prepareBaseGetListParams(params);
        queryParams.append("only_active", params.showOnlyActive || false);

        // *** NEW: server-side identifier filtering (see
        // PortalPlanogramPendingsController / PlanogramPendingsCriteria on
        // the backend). Lets callers like Device.tsx ask for a single
        // device's pending planograms directly, instead of paginating
        // through the whole merchant's list and filtering client-side.
        if (params.deviceId) {
            queryParams.append("device_id", params.deviceId);
        }

        if (params.planogramId) {
            queryParams.append("planogram_id", params.planogramId);
        }

        return fetchWithAuth(`${url}?${queryParams}`, {
            method: "GET",
        });
    },

    getStatusList: () => {
        const url = `/portal/planogram-pendings/status-list`;

        return fetchWithAuth(url, {
            method: "GET",
        });
    },

    copyToDevicesFromParent: (
        planogramId,
        planogramToUpdate,
        deviceIds,
        implementationDate,
        applyImmediately,
    ) => {
        const url = `/portal/planogram-pendings/copy-to-devices/${planogramId}`;
        const effectiveImplementationDate = implementationDate || new Date();

        const payload = {
            planogram: planogramToUpdate,
            deviceIds,
            applyImmediately: Boolean(applyImmediately),
            implementationDate: moment(effectiveImplementationDate).format(Constants.DATE_FORMAT),
        };

        return fetchWithAuth(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });
    },

    copyToDevicesFromChild: (
        planogramId,
        planogramToUpdate,
        deviceIds,
        implementationDate,
        applyImmediately,
    ) => {
        const url = `/portal/planogram-pendings/copy-to-devices-from-child/${planogramId}`;
        const effectiveImplementationDate = implementationDate || new Date();

        const payload = {
            planogram: planogramToUpdate,
            deviceIds,
            applyImmediately: Boolean(applyImmediately),
            implementationDate: moment(effectiveImplementationDate).format(Constants.DATE_FORMAT),
        };

        return fetchWithAuth(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });
    },

    getLocationsList: (
        columns,
        rows,
        deviceExternalIdToExclude,
        planogramData,
    ) => {
        const url = `/portal/planogram-pendings/locations-list`;

        const requestBody = {
            deviceExternalIdToExclude: deviceExternalIdToExclude ?? null,
            columns,
            rows,
            planogram: preparePlanogramForRequest(planogramData),
        };

        return fetchWithAuth(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
        });
    },

    getDevicesList: (
        columns,
        rows,
        locationExternalId,
        planogramExternalIdToExclude,
        deviceExternalIdToExclude,
        planogramData,
    ) => {
        const url = `/portal/planogram-pendings/devices-list`;

        const requestBody = {
            locationExternalId: locationExternalId ?? null,
            planogramExternalIdToExclude: planogramExternalIdToExclude ?? null,
            deviceExternalIdToExclude: deviceExternalIdToExclude ?? null,
            columns,
            rows,
            planogram: preparePlanogramForRequest(planogramData),
        };

        return fetchWithAuth(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
        });
    },

    getDetails: planogramId => {
        const url = `/portal/planogram-pendings/${planogramId}/details`;

        return fetchWithAuth(url, {
            method: "GET",
        });
    },

    delete: planogramId => {
        const url = `/portal/planogram-pendings/${planogramId}`;

        return fetchWithAuth(url, {
            method: "DELETE",
        });
    },

    markAsDone: planogramId => {
        const url = `/portal/planogram-pendings/${planogramId}/mark-as-done`;

        return fetchWithAuth(url, {
            method: "PUT",
        });
    },
};